<script lang="ts">
import type { ProductImage } from '@convatec/180medical-website/types/product-image'
import { getPrimaryImageIndex } from '~/utils/getPrimaryImage'

export interface ImageCarouselBlockProps {
  images: ProductImage[]
}
</script>

<script setup lang="ts">
const props = defineProps<ImageCarouselBlockProps>()

// -1 (no images) can't reach nextSlide/prevSlide — the template's v-if guards it.
const currentIndex = ref(getPrimaryImageIndex(props.images))
const hasMultipleImages = computed(() => props.images.length > 1)

function nextSlide() {
  currentIndex.value = (currentIndex.value + 1) % props.images.length
}

function prevSlide() {
  currentIndex.value = (currentIndex.value - 1 + props.images.length) % props.images.length
}

function goToSlide(index: number) {
  currentIndex.value = index
}
</script>

<template>
  <!-- contain-inline-size: keep the translated slide track's intrinsic width
       (sum of all slides) from propagating to ancestors and widening the
       mobile layout viewport — overflow-hidden only clips paint. -->
  <div
    v-if="images?.length"
    class="relative w-full overflow-hidden contain-inline-size"
  >
    <!-- Main carousel container -->
    <div class="relative">
      <!-- Images container with transition -->
      <div
        class="flex transition-transform duration-300 ease-in-out"
        :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
      >
        <div
          v-for="(image, index) in images"
          :key="index"
          class="w-full flex-shrink-0"
        >
          <NuxtImg
            provider="fastlyAprimo"
            :src="image.url"
            :alt="image.altText"
            class="w-full aspect-square object-contain"
          />
        </div>
      </div>

      <!-- Navigation arrows -->
      <button
        v-if="hasMultipleImages"
        class="absolute left-2 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white/90 rounded-full p-2 shadow-md"
        aria-label="Previous slide"
        @click="prevSlide"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
      <button
        v-if="hasMultipleImages"
        class="absolute right-2 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white/90 rounded-full p-2 shadow-md"
        aria-label="Next slide"
        @click="nextSlide"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>
    </div>

    <!-- Indicators -->
    <div
      v-if="hasMultipleImages"
      class="flex justify-center mt-4 space-x-2"
    >
      <button
        v-for="(_, index) in images"
        :key="index"
        class="group flex w-6 h-6 items-center justify-center rounded-full"
        :aria-label="`Go to image ${index + 1}`"
        @click="goToSlide(index)"
      >
        <span
          class="w-3 h-3 rounded-full transition-colors duration-200"
          :class="currentIndex === index ? 'bg-blue-600' : 'bg-gray-300 group-hover:bg-gray-400'"
        />
      </button>
    </div>
  </div>
</template>
